import React, { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { trpc, trpcQuery } from "@/hooks/trpc";
import { queryClient } from "@/hooks/query";
import type { FermentationGroupId, Fermentation } from "@brewbase/db/types";
import { cn } from "@/lib/utils";
import { Loader2, AlertCircle, PanelLeft, PanelRight, Upload, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import FermentationDataUploadDialog from "@/features/data-upload/fermentation-data-upload";

import {
  generateSeriesColors,
  type FermentationInfo,
  type NonNullColorCode,
} from "@/lib/color-utils";
import { useDataSources } from "@/features/fermentation-charts/data-sources";
import { useOnlineEnvelope } from "@/features/fermentation-charts/data-sources/useOnlineEnvelope";
import { useOnlineSelectionStats } from "@/features/fermentation-charts/data-sources/useOnlineSelectionStats";
import { useOnlineSparklines } from "@/features/fermentation-charts/data-sources/useOnlineSparklines";
import { describeLoadFailures } from "@/features/fermentation-charts/data-sources/describe-load-failures";
import {
  TimeseriesChart,
  VariableBrowserPanel,
  ChartToolbar,
  SelectionStatsPanel,
  useBundles,
  useChartViewState,
  type SelectionState,
  type SelectionRange,
  type TimeseriesChartRef,
} from "@/features/fermentation-charts/core";

export const Route = createFileRoute("/fermentations/$group_id/results/charts")({
  component: ChartsPage,
});

function ChartsPage() {
  const { group_id: groupIdParam } = Route.useParams();
  const navigate = useNavigate();
  const groupId = Number(groupIdParam) as FermentationGroupId;
  const [uploadOpen, setUploadOpen] = useState(false);

  // Fetch fermentations in group
  const { data: fermentations, isLoading: fermsLoading } =
    trpc.fermentations.list.useQuery({ groupId });

  // Fetch available attributes
  const {
    data: availableAttrs,
    isLoading: attrsLoading,
    error: attrsError,
  } =
    trpc.fermentationData.getAvailableAttributes.useQuery(
      { fermentationGroupIds: [groupId] },
      { enabled: !!groupId },
    );

  // Samples, weights and KPIs from their data sources. Online data comes
  // decimated from the server instead (useOnlineEnvelope below).
  const {
    data: sourceData,
    variableMetadata,
    isLoading: dataLoading,
    failures: sourceFailures,
    resetAll,
  } = useDataSources([groupId], { online: false });

  // Build bundles and color maps
  const [search, setSearch] = React.useState("");
  const { bundlesByKey, bundles, variableColorMap } = useBundles({
    availableAttrs,
    variableMetadata,
    search,
  });

  // Build series color map from fermentations
  const seriesColorMap = React.useMemo(() => {
    if (!fermentations) return {};
    const infos: FermentationInfo[] = fermentations.map((f: Fermentation) => ({
      id: f.id as number,
      name: f.name || `F-${f.id}`,
      color: (f.color_code ?? null) as NonNullColorCode | null,
    }));
    return generateSeriesColors(infos);
  }, [fermentations]);

  // Chart view state with localStorage persistence
  const {
    state: viewState,
    toggleVariant,
    toggleFermVisibility,
    toggleFineVisibility,
    setColorMode,
  } = useChartViewState({
    groupId,
    availableBundles: bundles,
  });

  // Series are keyed by fermentation name, as in the colour map
  const seriesNames = React.useMemo(
    () =>
      new Map(
        (fermentations ?? []).map((f: Fermentation) => [
          f.id as number,
          f.name || `F-${f.id}`,
        ]),
      ),
    [fermentations],
  );

  // The online attributes the chart draws: the selected variants of bundles
  // read from the online source
  const onlineAttributeNames = React.useMemo(() => {
    const names = new Set<string>();
    for (const sel of viewState.selectedVariants) {
      const bundle = bundlesByKey[sel.key];
      const name = bundle?.variants[sel.variant];
      const sourceId = bundle?.dataSourceId ?? "fermentation-online";
      if (name && sourceId === "fermentation-online") names.add(name);
    }
    return [...names];
  }, [viewState.selectedVariants, bundlesByKey]);

  // The chart's x window once the user stops zooming, for detail fetching
  const [zoomH, setZoomH] = React.useState<[number, number] | null>(null);

  const online = useOnlineEnvelope({
    groupId,
    attributeNames: onlineAttributeNames,
    seriesNames,
    zoomH,
  });

  const data = React.useMemo(
    () =>
      sourceData ? { ...sourceData, "fermentation-online": online.data } : null,
    [sourceData, online.data],
  );

  // Selection state
  const [selection, setSelection] = React.useState<SelectionState | null>(null);

  const isSeriesVisible = React.useCallback(
    (seriesName: string, attributeName: string) =>
      viewState.fermVisibility[seriesName] !== false &&
      viewState.fineVisibility[`${seriesName}::${attributeName}`] !== false,
    [viewState.fermVisibility, viewState.fineVisibility],
  );

  // Sparklines preview each online bundle's value variant (what the variable
  // browser draws), whether or not it is selected. From every bundle, not
  // the search-filtered list, so typing a search does not refetch them.
  const sparklineAttributeNames = React.useMemo(
    () =>
      Object.values(bundlesByKey).flatMap((b) =>
        (b.dataSourceId ?? "fermentation-online") === "fermentation-online" &&
        b.variants.value
          ? [b.variants.value]
          : [],
      ),
    [bundlesByKey],
  );
  const onlineSparklines = useOnlineSparklines({
    groupId,
    attributeNames: sparklineAttributeNames,
    seriesNames,
  });
  const sparklineData = React.useMemo(
    () => ({ ...sourceData, "fermentation-online": onlineSparklines }),
    [sourceData, onlineSparklines],
  );

  const onlineStats = useOnlineSelectionStats({
    groupId,
    windowH: selection?.active ? [selection.xMin, selection.xMax] : null,
    attributeNames: onlineAttributeNames,
    seriesNames,
    isVisible: isSeriesVisible,
  });

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  // Chart ref for brush actions
  const chartRef = React.useRef<TimeseriesChartRef>(null);

  // Handle brush selection. The chart holds online series decimated, so
  // their statistics come from the server (onlineStats); the selection keeps
  // only the rows of other sources, whose points are all here.
  const handleBrushSelect = React.useCallback(
    (sel: SelectionState) => {
      const online = new Set(onlineAttributeNames);
      setSelection({
        ...sel,
        rows: sel.rows.filter((r) => !online.has(r.variableId)),
        seriesIds: sel.seriesIds.filter(
          (id) => !online.has(id.slice(id.lastIndexOf("::") + 2)),
        ),
      });
    },
    [onlineAttributeNames],
  );

  const handleRefresh = React.useCallback(() => {
    resetAll();
    queryClient.invalidateQueries({
      queryKey: trpcQuery.fermentationData.pathKey(),
    });
  }, [resetAll]);

  // Handle brush zoom
  const handleBrushZoom = React.useCallback((range: SelectionRange) => {
    chartRef.current?.zoomToRange(range);
  }, []);

  // Close selection panel
  const handleCloseSelection = React.useCallback(() => {
    setSelection(null);
  }, []);

  // Open in canvas explorer
  const handleOpenInExplorer = React.useCallback(() => {
    // Navigate to canvas with this group pre-selected
    navigate({
      to: "/fermentations/canvas",
      search: {}, // Could pass group_id if canvas supports it
    });
  }, [navigate]);

  const isLoading = fermsLoading || attrsLoading || dataLoading;
  const hasData =
    (availableAttrs?.length ?? 0) > 0 ||
    Object.values(sourceData ?? {}).some((d) => Object.keys(d).length > 0);
  // Anything that failed to load, from the sources, the online attribute
  // list or the online envelope, each cause named once. Shown with a retry,
  // never as "no data".
  const loadError = describeLoadFailures([
    ...sourceFailures,
    ...(attrsError ? [{ what: "the online variable list", error: attrsError }] : []),
    ...(online.error ? [{ what: "online chart data", error: online.error }] : []),
  ]);

  // Loading failed and nothing came through: an error, never "no data"
  if (!isLoading && loadError && !hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-600 mb-4 opacity-50" />
        <h3 className="text-base font-medium mb-2">Timeseries data could not be loaded</h3>
        <p className="text-sm text-muted-foreground max-w-md">{loadError}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3 text-xs h-7 gap-1.5"
          onClick={handleRefresh}
        >
          <RotateCw className="w-3 h-3" />
          Retry
        </Button>
      </div>
    );
  }

  // Empty state when no data
  if (!isLoading && !hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <AlertCircle className="w-12 h-12 text-muted-foreground mb-4 opacity-50" />
        <h3 className="text-lg font-medium mb-2">No timeseries data available</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          This fermentation group doesn't have any recorded timeseries data yet.
          Data will appear here once recordings are uploaded.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3 text-xs h-7 gap-1.5"
          onClick={() => setUploadOpen(true)}
        >
          <Upload className="w-3 h-3" />
          Upload Data
        </Button>
        <FermentationDataUploadDialog
          groupId={groupId}
          open={uploadOpen}
          onOpenChange={setUploadOpen}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar */}
      <ChartToolbar
        viewState={viewState}
        bundlesByKey={bundlesByKey}
        data={data}
        seriesColorMap={seriesColorMap}
        variableColorMap={variableColorMap}
        onColorModeChange={setColorMode}
        onToggleFermVisibility={toggleFermVisibility}
        onToggleFineVisibility={toggleFineVisibility}
        onRefresh={handleRefresh}
        onBrushSelect={() => chartRef.current?.beginBrush("lineX", "select")}
        onBrushZoom={() => chartRef.current?.beginBrush("rect", "zoom")}
        onOpenInExplorer={handleOpenInExplorer}
        extraActions={
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={() => setUploadOpen(true)}
            title="Upload data files"
          >
            <Upload className="h-3.5 w-3.5" />
            <span>Upload</span>
          </Button>
        }
        isLoading={isLoading || online.isLoading || online.isRefining}
      />

      <div className="flex flex-1 min-h-0">
        {/* Main chart area */}
        <div className="flex-1 relative min-w-0">
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <TimeseriesChart
              data={data}
              bundlesByKey={bundlesByKey}
              viewState={viewState}
              seriesColorMap={seriesColorMap}
              variableColorMap={variableColorMap}
              onBrushSelect={handleBrushSelect}
              onBrushZoom={handleBrushZoom}
              onXZoomChange={setZoomH}
              chartRef={chartRef}
              className="w-full h-full"
            />
          )}

          {/* Some data failed while the rest loaded */}
          {!isLoading && loadError && (
            <div className="absolute top-2 left-2 z-10 flex items-center gap-2 px-2 py-1 border border-olive-6 bg-olive-2 text-xs text-red-600">
              <AlertCircle className="w-3 h-3" />
              <span>{loadError}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-1 text-xs gap-1"
                onClick={handleRefresh}
              >
                <RotateCw className="w-3 h-3" />
                Retry
              </Button>
            </div>
          )}

          {/* Selection stats panel (floating) */}
          {selection && selection.active && (
            <div className="absolute bottom-4 left-4 z-10 max-w-lg">
              <SelectionStatsPanel
                selection={selection}
                extraStats={onlineStats.stats}
                extraStatsPending={onlineStats.isPending}
                extraStatsError={onlineStats.error}
                onClose={handleCloseSelection}
              />
            </div>
          )}
        </div>

        {/* Sidebar toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={cn(
            "flex-shrink-0 w-6 flex items-center justify-center",
            "border-l border-olive-6 hover:bg-olive-3 transition-colors",
          )}
          title={sidebarOpen ? "Hide variable browser" : "Show variable browser"}
        >
          {sidebarOpen ? (
            <PanelRight className="w-3.5 h-3.5 text-muted-foreground" />
          ) : (
            <PanelLeft className="w-3.5 h-3.5 text-muted-foreground" />
          )}
        </button>

        {/* Variable browser sidebar */}
        {sidebarOpen && (
          <div className="w-64 flex-shrink-0 border-l border-olive-6 bg-olive-1">
            <VariableBrowserPanel
              bundles={bundles}
              selectedVariants={viewState.selectedVariants}
              variableColorMap={variableColorMap}
              data={sparklineData}
              search={search}
              onSearchChange={setSearch}
              onToggleVariant={toggleVariant}
              className="h-full"
            />
          </div>
        )}
      </div>

      <FermentationDataUploadDialog
        groupId={groupId}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />
    </div>
  );
}
